<template>
  <div>
    <breadcrumb-header
      :items="breadcrumbs"
      :help-link="helpPageLink"
    />
    <div v-if="dayOptions && type == 'day'">
      <div
        v-for="(day, index) in dayOptions"
        :key="index"
      >
        <div class="text-headline-medium mb-2">
          <span v-if="index === 'Today'">
            {{ t('date.today') }}
          </span>
          <span v-else>
            {{ t(`dow.${dayOverseerDayToDay(index)}`) }}
          </span>
        </div>
        <v-row>
          <v-col
            v-for="(dayOverseer, dayOverseerIndex) in day"
            :key="dayOverseerIndex"
            class="text-center"
            cols="12"
            sm="6"
            md="6"
            lg="4"
          >
            <day-overseer-card :day-overseer="dayOverseer" />
          </v-col>
        </v-row>
        <v-divider
          class="my-4"
        />
      </div>
    </div>
    <div v-if="locationOptions && type == 'location'">
      <div
        v-for="(location, index) in locationOptions"
        :key="index"
      >
        <div class="text-headline-medium mb-2">
          {{ location.name }}
        </div>
        <v-row>
          <v-col
            v-for="(dayOverseer, dayOverseerIndex) in todaysLocationOverseers(location.day_overseers)"
            :key="dayOverseerIndex"
            class="text-center"
            cols="12"
            sm="6"
            md="4"
          >
            <day-overseer-card
              :day-overseer="dayOverseer"
              title-type="today"
            />
          </v-col>

          <v-col
            v-for="(entry, i) in groupDayOverseers(location.day_overseers)"
            :key="i"
            class="text-center"
            cols="12"
            sm="6"
            md="4"
          >
            <day-overseer-card
              :day-overseer="entry.overseer"
              title-type="combined"
              :title="formatWeekdayRanges(entry.days)"
            />
          </v-col>
        </v-row>
        <v-divider
          class="my-4"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import axios from 'axios';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDate, useSettings } from '@/composables';
import { useErrorStore } from '@/stores/errors';
import DayOverseerCard from './DayOverseerCard.vue';

const { t } = useI18n();
const { handleError } = useErrorStore();
const { getSetting } = useSettings();

const { getIsoWeekday } = useDate();

const type = getSetting('day_overseer_display_type');

const breadcrumbs = computed(() => [{ title: type === 'day' ? t('dayoverseer.title', 2) : t('dayoverseer.location_overseer', 2) }]);

interface DayOverseerList {
  Monday: App.Models.DayOverseer[];
  Tuesday: App.Models.DayOverseer[];
  Wednesday: App.Models.DayOverseer[];
  Thursday: App.Models.DayOverseer[];
  Friday: App.Models.DayOverseer[];
  Saturday: App.Models.DayOverseer[];
  Sunday: App.Models.DayOverseer[];
  Today: App.Models.DayOverseer[];
  BankHoliday: App.Models.DayOverseer[];
}

const isHoliday = ref<boolean>(false);
const dayOptions = ref<DayOverseerList>();
const locationOptions = ref<App.ApiResources.LocationResource[]>();

const helpPageLink = computed(() => {
  if (type === 'location') {
    return '/docs/publisher/before-shifts/location-overseers';
  }
  return '/docs/publisher/before-shifts/day-overseers';
});
async function loadData() {
  try {
    const response = await axios.get('/api/dayoverseers');
    dayOptions.value = response.data.data;
    const locationResponse = await axios.get('/api/dayoverseers/locations');
    locationOptions.value = locationResponse.data.data.locations;
    isHoliday.value = response.data.data.is_holiday_today;
  }
  catch (error) {
    handleError(error, t('dayoverseer.cannot_load'));
  }
}
function groupDayOverseers(overseers: App.Models.DayOverseer[]) {
  const map = new Map();

  for (const o of overseers) {
    const key = o.user_id; // or any unique composite identifier
    if (!map.has(key)) {
      map.set(key, { overseer: o, days: [t(`dow.${o.day_of_week}`)] });
    }
    else {
      map.get(key).days.push(t(`dow.${o.day_of_week}`));
    }
  }

  return Array.from(map.values());
}

const weekdayOrder = [
  t('dow.1'),
  t('dow.2'),
  t('dow.3'),
  t('dow.4'),
  t('dow.5'),
  t('dow.6'),
  t('dow.7'),
  t('dow.0'),
];

function dayOverseerDayToDay(day: string): number {
  if (day === 'Today') {
    return getIsoWeekday();
  }
  switch (day) {
    case 'Monday':
      return 1;
    case 'Tuesday':
      return 2;
    case 'Wednesday':
      return 3;
    case 'Thursday':
      return 4;
    case 'Friday':
      return 5;
    case 'Saturday':
      return 6;
    case 'Sunday':
      return 7;
    default:
      return 0;
  }
}

function formatWeekdayRanges(days: string[]): string {
  const indices = days
    .map(d => weekdayOrder.indexOf(d))
    .filter(i => i >= 0)
    .sort((a, b) => a - b);

  const ranges: string[] = [];
  let start = indices[0];
  let end = indices[0];

  for (let i = 1; i < indices.length; i++) {
    const current = indices[i];
    if (current === end + 1) {
      end = current;
    }
    else {
      ranges.push(buildRange(start, end));
      start = current;
      end = current;
    }
  }
  ranges.push(buildRange(start, end));

  return ranges.join(', ');
}

function buildRange(startIdx: number, endIdx: number): string {
  const startLabel = weekdayOrder[startIdx];
  const endLabel = weekdayOrder[endIdx];
  return startIdx === endIdx ? startLabel : `${startLabel}–${endLabel}`;
}

function todaysLocationOverseers(dayOverseers): App.Models.DayOverseer[] {
  const today = getIsoWeekday();
  const holidayOverseers = dayOverseers.filter(o => o.day_of_week === 0);
  if (isHoliday.value && holidayOverseers.length > 0) {
    return holidayOverseers;
  }
  return dayOverseers.filter(o => o.day_of_week === today);
}

loadData();
</script>
